import { Mail, Phone, MapPin, Clock, Facebook, Instagram, Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import { trackLead } from "@/lib/analytics";
import { COMPANY } from "@/components/SEO";

export default function Footer() {
  return (
    <footer className="bg-[var(--vp-primary)] text-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid md:grid-cols-12 gap-10">
        {/* Brand column — white logo PNG on dark footer */}
        <div className="md:col-span-5">
          <div className="mb-5" data-testid="footer-logo">
            <img
              src="/logo.png?v=blue1"
              alt="Vacations Pro"
              className="h-20 md:h-24 w-auto object-contain"
            />
          </div>
          <p className="text-xl md:text-2xl leading-snug max-w-md text-white/90 font-medium">
            Private journeys, planned by people who still remember what it felt like to arrive somewhere new.
          </p>
          <div className="flex items-center gap-3 mt-6">
            <a
              href="https://wa.me/919501030117?text=Hello%20Vacations%20Pro"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackLead({ source: "footer_whatsapp" })}
              className="bg-[#25D366] hover:bg-[#1ebe5d] text-white rounded-full px-5 py-2.5 text-xs tracking-[0.15em] uppercase font-medium transition-colors"
              data-testid="footer-whatsapp"
            >
              Chat on WhatsApp
            </a>
            <a
              href="https://www.facebook.com/vacationspro/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="w-10 h-10 rounded-full border border-white/25 flex items-center justify-center hover:bg-white hover:text-[var(--vp-primary)] transition-colors"
              data-testid="footer-fb"
            >
              <Facebook className="w-4 h-4" strokeWidth={1.5} />
            </a>
            <a
              href="https://www.instagram.com/vacationsproofficial"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="w-10 h-10 rounded-full border border-white/25 flex items-center justify-center hover:bg-white hover:text-[var(--vp-primary)] transition-colors"
              data-testid="footer-ig"
            >
              <Instagram className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </div>
        </div>

        {/* Contact */}
        <div className="md:col-span-4">
          <div className="vp-label text-white/60 mb-4">GET IN TOUCH</div>
          <ul className="space-y-3 text-sm text-white/85">
            <li className="flex items-start gap-3">
              <Building2 className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
              <span data-testid="footer-legal-name">{COMPANY.legalName}</span>
            </li>
            {COMPANY.phones.map((p, i) => (
              <li key={p.tel} className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
                <a href={`tel:${p.tel}`} className="hover:text-white" data-testid={i === 0 ? "footer-phone" : `footer-phone-${i + 1}`}>
                  {p.display}
                </a>
              </li>
            ))}
            <li className="flex items-start gap-3">
              <Mail className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
              <a href={`mailto:${COMPANY.email}`} className="hover:text-white" data-testid="footer-email">
                {COMPANY.email}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
              <span data-testid="footer-address">
                {COMPANY.addressLines[0]}<br />
                {COMPANY.addressLines[1]}
              </span>
            </li>
            <li className="flex items-start gap-3">
              <Clock className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
              <span>Mon – Sat · 8:45 AM – 7:15 PM</span>
            </li>
          </ul>
        </div>

        {/* Studio */}
        <div className="md:col-span-3">
          <div className="vp-label text-white/60 mb-4">STUDIO</div>
          <ul className="space-y-2 text-sm text-white/85">
            <li><Link to="/about" className="hover:text-white" data-testid="footer-about">About Us</Link></li>
            <li><Link to="/admin" className="hover:text-white">Seller Portal</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/15">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 text-xs text-white/50 flex items-center justify-between flex-wrap gap-3">
          <span data-testid="footer-copyright">© {new Date().getFullYear()} {COMPANY.legalName} · Founded in {COMPANY.founded}</span>
          <span className="tracking-wider">MADE WITH CARE · NOT WITH HASTE</span>
        </div>
      </div>
    </footer>
  );
}
